import { createTheme, ThemeOptions } from "@mui/material/styles";

const getDesignTokens = (mode: "light" | "dark"): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
          // Light Mode
          primary: {
             main: "#6366f1", // Indigo 500
             contrastText: "#ffffff",
          },
          secondary: {
            main: "#8b5cf6", // Violet 500
          },
          background: {
            default: "#f3f4f6", // Cool grey background
            paper: "#ffffff",
          },
          text: {
            primary: "#111827",
            secondary: "#4b5563",
          },
          divider: "#e5e7eb",
          action: {
            hover: "rgba(99, 102, 241, 0.04)",
          }
        }
      : {
          // Dark Mode
          primary: {
            main: "#818cf8", // Indigo 400
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#a78bfa", // Violet 400
          },
          background: {
            default: "#0f172a", // Slate 900
            paper: "#1e293b", // Slate 800
          },
          text: {
            primary: "#f9fafb",
            secondary: "#94a3b8",
          },
          divider: "#334155",
          action: {
            hover: "rgba(129, 140, 248, 0.08)",
          }
        }),
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    h1: {
      fontSize: "2.25rem",
      fontWeight: 800,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "1.875rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    h4: {
        fontSize: "1.25rem",
        fontWeight: 700,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
    subtitle2: {
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    }
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          padding: "10px 20px",
          boxShadow: "none",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0px)",
          }
        },
        contained: {
          "&:hover": {
            boxShadow: "0 6px 16px rgba(99, 102, 241, 0.3)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "20px",
          boxShadow: mode === 'light' 
            ? "0 2px 8px rgba(0,0,0,0.04)" 
            : "0 2px 8px rgba(0,0,0,0.2)",
          border: "none",
          backgroundImage: "none",
          transition: "all 0.2s ease-in-out",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                borderRadius: "24px",
            }
        }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          backgroundColor: mode === "light" ? "#f9fafb" : "#334155", 
          "& fieldset": {
            border: "1px solid transparent",
            transition: "all 0.2s",
          },
          "&:hover fieldset": {
             borderColor: mode === "light" ? "#d1d5db" : "#475569",
          },
          "&.Mui-focused fieldset": {
            border: `2px solid ${mode === "light" ? "#6366f1" : "#818cf8"}`, 
          },
          "&.Mui-focused": {
            backgroundColor: mode === "light" ? "#ffffff" : "#1e293b",
          }
        },
        input: {
           padding: "14px 16px",
        }
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: mode === "light" ? "1px solid rgba(0,0,0,0.05)" : "1px solid rgba(255,255,255,0.05)",
          backgroundColor: mode === "light" ? "rgba(255,255,255,0.8)" : "rgba(15, 23, 42, 0.8)",
          backdropFilter: "blur(12px)",
          color: mode === "light" ? "#111827" : "#F9FAFB",
          backgroundImage: "none",
        },
      },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                fontWeight: 600,
                borderRadius: "8px",
            }
        }
    }
  },
});

export function buildTheme(mode: "light" | "dark") {
  return createTheme(getDesignTokens(mode));
}
